import cubeClient from "../../cubeClient"
import { getProgramCourseMapping } from "../../../snapcourse/authoring/components/SnapCourseConfigForm/components/ProgramCourseDropdown/helpers"
import { REQUEST_PARAMS } from "../../constants"
import { Column } from "../../components/Table"
import { FiltersState } from "../../states"
import { ProgramAndCoursesInfo } from "../../../../types/gqlTypes/graphql"
import { CubeRequestParams } from "../../types"
import { getEACTScores, isBooleanLikeTrue } from "../scores"

const getSubjectScoreScaleField = (currentViewName: string, subject: string) =>
  `${currentViewName}.scale/${subject.replaceAll(" ", "_")}`

export const getTransformedScoresTableData = (
  idProp: string,
  filtersState: FiltersState,
  programsAndCourses: ProgramAndCoursesInfo[],
  records: any[],
  columns: Column[],
  properties: string[]
) => {
  const currentViewName = idProp?.split(".")[0] || "scores_view"

  const filterTestValues = filtersState.test || []
  const filterContentValues = filtersState.content || []

  const programsSubjectsMapping = getProgramCourseMapping(filterTestValues)

  const selectedPrograms = Object.keys(programsSubjectsMapping || {})

  if (
    !!filterContentValues?.length &&
    filterContentValues.every(content => content.includes("Mastery Lesson"))
  ) {
    const transformedColumns = columns.map(column => {
      if (["Score", "Scale Score"].includes(column.label)) {
        return {
          ...column,
          label: "Accuracy",
          name: `${currentViewName}.percentage`
        }
      }

      return column
    })
    const transformedProperties = properties.map(property => {
      if (property === `${currentViewName}.scale`) {
        return `${currentViewName}.percentage`
      }

      return property
    })

    return {
      columns: transformedColumns,
      properties: transformedProperties,
      records
    }
  }

  if (selectedPrograms.length === 1) {
    const selectedProgram = selectedPrograms[0]
    const programSubjects = !!programsSubjectsMapping[selectedProgram]?.length
      ? programsSubjectsMapping[selectedProgram]
      : programsAndCourses
          .find(item => item.program === selectedProgram)
          ?.courses.map(item => item.name) || []

    const programSubjectsColumns: Column[] = programSubjects.map(subject => ({
      label: `${subject} Score`,
      name: getSubjectScoreScaleField(currentViewName, subject),
      sortable: false,
      type: "link",
      linkUrl: "",
      linkTooltip: () => "View Performance"
    }))
    const programSubjectProperties: string[] = programSubjects.map(subject =>
      getSubjectScoreScaleField(currentViewName, subject)
    )

    const transformedColumns = columns.map(column => {
      if (column.label === "Score") {
        return {
          ...column,
          label: "Scale Score"
        }
      }

      return column
    })

    const transformedRecordsWithSubjects = records.map(item => {
      const subtests = JSON.parse(
        item[`${currentViewName}.subtestScores`] || "[]"
      )

      if (!!subtests?.length) {
        const programSubjectsObject = programSubjects.reduce((res, subject) => {
          const currentSubtest = subtests.find(
            subtest => subtest.subject === subject
          )

          if (!!currentSubtest) {
            if (
              item[`${currentViewName}.program`] === "EACT" &&
              isBooleanLikeTrue(
                item[`${currentViewName}.isDiagnosticForACT`]
              )
            ) {
              return {
                ...res,
                [getSubjectScoreScaleField(currentViewName, subject)]:
                  getEACTScores(Number(currentSubtest.scale))
              }
            }

            return {
              ...res,
              [getSubjectScoreScaleField(currentViewName, subject)]:
                currentSubtest.scale
            }
          }

          return res
        }, {})

        return {
          ...item,
          ...programSubjectsObject
        }
      }

      return {
        ...item
      }
    })

    const columnWithSubjects = [
      ...transformedColumns,
      ...programSubjectsColumns
    ]
    const propertiesWithSubjects = [...properties, ...programSubjectProperties]

    return {
      columns: columnWithSubjects,
      properties: propertiesWithSubjects,
      records: transformedRecordsWithSubjects
    }
  } else {
    return { columns, properties, records }
  }
}

export const fetchAdditionalScoresTableData = async (details: {
  page: string
  subpage: string
  filters: FiltersState
  basicDataTablePromise: Promise<any>
  requestParams: Record<string, Record<string, CubeRequestParams>>
}): Promise<any> => {
  const { page, subpage, filters, basicDataTablePromise } = details || {}

  const filterTestValues = filters.test || []

  const programsSubjectsMapping = getProgramCourseMapping(filterTestValues)
  const selectedPrograms = Object.keys(programsSubjectsMapping || {}).length

  if (selectedPrograms === 1) {
    const basicTableData = await basicDataTablePromise

    const dataIds = Array.from<string>(
      new Set(
        basicTableData
          .filter(item => !!item)
          .map(
            item =>
              `${item["scores_view.studentId"]}/${item["scores_view.testId"]}`
          )
      )
    ).reduce<{ testIds: string[]; studentIds: string[] }>(
      (res, item) => {
        const [studentId, testId] = item.split("/")

        return {
          testIds: [...res.testIds, ...(!!testId ? [testId] : [])],
          studentIds: [...res.studentIds, ...(!!studentId ? [studentId] : [])]
        }
      },
      { testIds: [], studentIds: [] }
    )

    if (!dataIds?.testIds?.length || !dataIds?.studentIds?.length) {
      return []
    }

    return cubeClient
      .get("/", {
        params: {
          query: JSON.stringify({
            ...(REQUEST_PARAMS[page]?.[`subtests-${subpage}`] || {}),
            filters: [
              {
                member: "scores_subtests_view.testId",
                operator: "equals",
                values: [...dataIds.testIds]
              },
              {
                member: "scores_subtests_view.studentId",
                operator: "equals",
                values: [...dataIds.studentIds]
              }
            ]
          })
        }
      })
      .then((res: any) => {
        return basicTableData.map(item => {
          const currentTestSubtests = res.filter(
            subtest =>
              item["scores_view.testId"] ===
                subtest["scores_subtests_view.testId"] &&
              item["scores_view.studentId"] ===
                subtest["scores_subtests_view.studentId"]
          )
          return !!currentTestSubtests?.length
            ? {
                ...item,
                meta: {
                  subtests: currentTestSubtests
                }
              }
            : item
        })
      })
  }

  return basicDataTablePromise
}

export const fetchAdditionalSubtestsScoresTableData = async (details: {
  page: string
  subpage: string
  filters: FiltersState
  basicDataTablePromise: Promise<any>
  requestParams: Record<string, Record<string, CubeRequestParams>>
}): Promise<any> => {
  const { basicDataTablePromise } = details || {}

  const basicTableData = await basicDataTablePromise

  // Collect unique subtestIds to load grade levels
  const subtestIds = Array.from<string>(
    new Set(
      basicTableData
        .filter(item => !!item?.["scores_subtests_view.subtestId"])
        .map(item => item["scores_subtests_view.subtestId"])
    )
  )

  if (!subtestIds.length) {
    return basicTableData
  }

  // Load grade levels data
  return cubeClient
    .get("/", {
      params: {
        query: JSON.stringify({
          dimensions: [
            "subtests_grade_levels.id",
            "subtests_grade_levels.gradeLevels",
            "subtests_grade_levels.gradeLevelNames",
            "subtests_grade_levels.category"
          ],
          filters: [
            {
              member: "subtests_grade_levels.id",
              operator: "equals",
              values: subtestIds
            }
          ]
        })
      }
    })
    .then((gradeLevelsData: any) => {
      return basicTableData.map(item => ({
        ...item,
        gradeLevel: gradeLevelsData.find(
          level =>
            level["subtests_grade_levels.id"] ===
            item["scores_subtests_view.subtestId"]
        )
      }))
    })
}

export const getScoresContentLink = (contentType: string): string => {
  return `/reporting/content/content-library/${contentType}`
}
